<template>
  <div class="social-buttons">
    <a
      v-if="mode !== 'register'"
      :href="googleUrl"
      class="social-btn social-btn--wide google"
    >
      <img :src="GoogleIcon" class="social-icon" alt="Google" />
      <span>{{ googleLabel }}</span>
    </a>
    <button
      v-else
      type="button"
      class="social-btn social-btn--wide google"
      :disabled="isLoading"
      @click="handleGoogleClick"
    >
      <img :src="GoogleIcon" class="social-icon" alt="Google" />
      <span>{{ googleLabel }}</span>
    </button>

    <button
      type="button"
      class="social-btn social-btn--wide telegram"
      :disabled="isLoading"
      @click="handleTelegramClick"
    >
      <img :src="TelegramIcon" class="social-icon" alt="Telegram" />
      <span>{{ telegramLabel }}</span>
    </button>

    <!-- Telegram Waiting Modal -->
    <TelegramLoginWaitingModal
      v-model="showModal"
      :status="modalStatus"
      :ttl="challengeTtl"
      :challenge-id="challengeId"
      :bot-username="telegramBotUsername"
      :error-reason="modalErrorReason"
      :light="light"
      @cancel="handleCancel"
      @retry="handleRetry"
      @close="handleClose"
    />

    <SponsorPopup v-model="showSponsorPopup" :light="light" @confirmed="onSponsorConfirmed" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import GoogleIcon from '@/assets/images/icons/google.svg'
import TelegramIcon from '@/assets/images/icons/telegram_icon.svg'
import TelegramLoginWaitingModal from './TelegramLoginWaitingModal.vue'
import SponsorPopup from './SponsorPopup.vue'
import { TelegramLoginPollingService } from '../services/TelegramLoginPollingService'
import type { TelegramTotpSignal } from '../services/TelegramLoginPollingService'
import { httpClient } from '@/shared/api/httpClient'
import { miniAppLinks, openTelegram } from '../utils/telegramDeepLink'

const props = defineProps<{
  /** Mode: 'login' or 'register' */
  mode?: 'login' | 'register'
  light?: boolean
  /** Optional inviter login for telegram referral link */
  inviterLogin?: string
}>()

const emit = defineEmits<{
  /** OTP-R6c: Telegram дал pending-вход, шаг TOTP показывает окно входа. */
  (e: 'totp-required', signal: TelegramTotpSignal): void
}>()

const { t } = useI18n()
const telegramBotUsername = ref('')

// ── State ───────────────────────────────────────

const isLoading = ref(false)
const showModal = ref(false)
const modalStatus = ref<'waiting' | 'success' | 'error'>('waiting')
const modalErrorReason = ref<'expired' | 'timeout' | 'error'>('error')
const challengeTtl = ref(300)
const challengeId = ref('')

let stopPolling: (() => void) | null = null
let generation = 0

// ── Sponsor Popup State ─────────────────────────

const showSponsorPopup = ref(false)
type PendingProvider = 'google' | 'telegram' | null
const pendingProvider = ref<PendingProvider>(null)

// ── Base58 (for Google state) ───────────────────

const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz'

const encodeBase58 = (str: string): string => {
  const bytes = new TextEncoder().encode(str)
  let num = 0n
  for (const byte of bytes) {
    num = num * 256n + BigInt(byte)
  }
  if (num === 0n) return BASE58_ALPHABET[0]
  let result = ''
  while (num > 0n) {
    const remainder = Number(num % 58n)
    result = BASE58_ALPHABET[remainder] + result
    num = num / 58n
  }
  for (const byte of bytes) {
    if (byte === 0) {
      result = BASE58_ALPHABET[0] + result
    } else {
      break
    }
  }
  return result
}

// ── Helpers ─────────────────────────────────────

const getReferralCode = () => {
  const stored = props.inviterLogin || localStorage.getItem('partner_referral_code')
  if (stored) return stored
  const match = document.cookie.match(/(^| )referal=([^;]+)/)
  return match ? decodeURIComponent(match[2]) : ''
}

const storageKey = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken'

// ── Computed ────────────────────────────────────

const buildGoogleUrl = (refCode?: string) => {
  const stateObj: Record<string, string> = {}
  if (refCode) stateObj.referal = refCode
  const state = Object.keys(stateObj).length > 0
    ? encodeBase58(JSON.stringify(stateObj))
    : 'AQ4'
  return props.mode === 'register'
    ? `/app/profile/google-signup?state=${state}`
    : `/app/profile/google-signin?state=${state}`
}

const googleUrl = computed(() => buildGoogleUrl(getReferralCode()))

const googleLabel = computed(() =>
  props.mode === 'register' ? t('auth.registerWithGoogle') : t('auth.loginWithGoogle'),
)

const telegramLabel = computed(() =>
  props.mode === 'register' ? t('auth.registerWithTelegram') : t('auth.loginWithTelegram'),
)

// ── Sponsor Popup Logic ─────────────────────────

const needsSponsorPopup = () => {
  return props.mode === 'register' && !getReferralCode()
}

const openSponsorPopup = (provider: PendingProvider) => {
  pendingProvider.value = provider
  showSponsorPopup.value = true
}

const onSponsorConfirmed = (login: string | null) => {
  const provider = pendingProvider.value
  pendingProvider.value = null

  if (provider === 'google') {
    window.location.href = buildGoogleUrl(login ?? undefined)
  } else if (provider === 'telegram') {
    startTelegramLogin(login ?? undefined)
  }
}

// ── Google Click Handler ────────────────────────

const handleGoogleClick = () => {
  if (needsSponsorPopup()) {
    openSponsorPopup('google')
  } else {
    window.location.href = googleUrl.value
  }
}

// ── Telegram Click Handler ──────────────────────

const handleTelegramClick = () => {
  if (isLoading.value) return
  if (props.mode === 'login') {
    startTelegramLogin(getReferralCode() || undefined)
    return
  }

  if (needsSponsorPopup()) {
    openSponsorPopup('telegram')
  } else {
    startTelegramLogin(getReferralCode() || undefined)
  }
}

// ── Telegram Login Flow ─────────────────────────

const cleanup = () => {
  generation++
  if (stopPolling) {
    stopPolling()
    stopPolling = null
  }
}

const startTelegramLogin = async (refCode?: string) => {
  cleanup()
  const attempt = generation

  isLoading.value = true
  try {
    const { challenge_id, ttl, bot } = await TelegramLoginPollingService.initChallenge(refCode)
    if (attempt !== generation) return
    telegramBotUsername.value = (bot || '').replace(/^@/, '')
    if (!telegramBotUsername.value) {
      console.warn('[GonkaGame][auth] Telegram bot username is not configured')
      modalStatus.value = 'error'
      modalErrorReason.value = 'error'
      showModal.value = true
      return
    }

    // Open the MiniApp directly, keeping this browser's challenge polling alive.
    openTelegram(miniAppLinks(telegramBotUsername.value, `tglogin_${challenge_id}`))

    // Show waiting modal
    challengeTtl.value = ttl
    challengeId.value = challenge_id
    modalStatus.value = 'waiting'
    modalErrorReason.value = 'error'
    showModal.value = true

    // Start polling
    stopPolling = TelegramLoginPollingService.startPolling(
      challenge_id,
      (token, status) => {
        // OTP-R6c: pending-токен — не вход. Не сохраняем его и не перезагружаем страницу.
        if (status.requires_approval) {
          if (status.approval_type === 'totp') {
            cleanup()
            showModal.value = false
            emit('totp-required', { ...status, token, approval_type: 'totp' })
          } else {
            modalStatus.value = 'error'
            modalErrorReason.value = 'error'
          }
          return
        }
        modalStatus.value = 'success'
        httpClient.setToken(token)
        localStorage.setItem(storageKey, token)
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      },
      (reason) => {
        modalStatus.value = 'error'
        modalErrorReason.value = reason
      },
    )
  } catch {
    if (attempt !== generation) return
    modalStatus.value = 'error'
    modalErrorReason.value = 'error'
    showModal.value = true
  } finally {
    if (attempt === generation) isLoading.value = false
  }
}

const handleCancel = () => {
  cleanup()
  showModal.value = false
}

const handleRetry = () => {
  showModal.value = false
  startTelegramLogin(getReferralCode() || undefined)
}

const handleClose = () => {
  cleanup()
  showModal.value = false
}

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped lang="scss">
.social-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.social-btn {
  width: 60px;
  height: 48px;
  border-radius: var(--p-radius-50);
  border: 1px solid var(--ds-border);
  background: var(--ds-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--ds-text-size-h-md);
  cursor: pointer;
  transition: background 0.2s;
  text-decoration: none;
  color: inherit;

  &:hover {
    background: var(--ds-surface-raised);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.social-btn--wide {
  width: 100%;
  gap: 10px;
  padding: 0 16px;
}

.social-btn--wide span {
  font-size: var(--ds-text-size-body);
  font-weight: var(--ds-weight-semibold);
}

.social-btn.google,
.social-btn.telegram {
  background: var(--ds-surface);
  color: var(--ds-text-secondary);
  border: 1px solid #ddd;

  &:hover:not(:disabled) {
    background: var(--ds-surface-raised);
    border-color: #ccc;
  }
}

.social-icon {
  width: 20px;
  height: 20px;
}
</style>
